// components/Materials/MaterialViewerModal.js
// ──────────────────────────────────────────────────────────────────────────────
// Full-screen overlay that renders any document type inline using ContentViewer.
// Used by BatchMaterialsPage (and any future page) to view materials in-app.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useCallback } from 'react';
import { X, Download, ExternalLink } from 'lucide-react';
import { AppButton } from '../ui';
import ContentViewer from '../SelfPaced/ContentViewer';
import AccessBadge from '../ContentProtection/AccessBadge';
import { canCopyFile, canDownloadFile } from '../ContentProtection/contentAccess';

// File types that can be viewed inline
const VIEWABLE_TYPES = new Set([
  'PDF', 'DOC', 'DOCX', 'PPT', 'PPTX', 'XLS', 'XLSX',
  'JPG', 'JPEG', 'PNG', 'GIF', 'SVG', 'WEBP', 'BMP',
  'TXT', 'CSV', 'HTML', 'HTM', 'MD',
]);

/**
 * Check whether a material's file type can be viewed inline.
 * @param {string} fileType - e.g. "PDF", "DOCX"
 * @returns {boolean}
 */
export const isViewableType = (fileType) => VIEWABLE_TYPES.has((fileType || '').toUpperCase());

/**
 * Map material `fileType` to the ContentViewer `type` prop.
 */
export const mapFileType = (ft) => {
  const t = (ft || '').toLowerCase();
  if (t === 'pdf') return 'pdf';
  if (t === 'ppt' || t === 'pptx') return 'ppt';
  if (t === 'doc' || t === 'docx') return 'docx';
  if (t === 'xls' || t === 'xlsx') return 'spreadsheet';
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(t)) return 'image';
  if (['txt', 'csv', 'md', 'html', 'htm'].includes(t)) return 'text';
  return 'fallback';
};

const MaterialViewerModal = ({ show, material, url, pdfUrl, onClose, onDownload }) => {
  // Close on Escape
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [show, handleKeyDown]);

  if (!show || !material) return null;

  const viewerType = mapFileType(material.fileType);
  const title = material.title || material.fileName || 'Document';
  // Content protection — decided by the server per file (vc_be/utils/contentAccess.js).
  const canDownload = canDownloadFile(material);
  const canCopy = canCopyFile(material);

  return (
    <div className="mv-overlay" onClick={onClose}>
      <div className="mv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mv-modal__header">
          <div className="mv-modal__title-row">
            <h3 className="mv-modal__title">{title}</h3>
            {material.fileType && (
              <span className="mv-modal__badge">{material.fileType}</span>
            )}
            <AccessBadge access={material.access} />
          </div>

          <div className="mv-modal__actions">
            {url && canDownload && (
              <>
                <AppButton
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload?.(material)}
                  title="Download"
                >
                  <Download size={14} />
                  Download
                </AppButton>
                <AppButton
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(url, '_blank')}
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                </AppButton>
              </>
            )}
            <button className="mv-modal__close" onClick={onClose} title="Close (Esc)">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ContentViewer */}
        <div className="mv-modal__body">
          {url ? (
            <ContentViewer
              type={viewerType}
              fileType={material.fileType?.toLowerCase()}
              url={url}
              // A presentation the server has converted to PDF shows as that PDF.
              pdfUrl={pdfUrl}
              title={title}
              fileName={material.fileName}
              canDownload={canDownload}
              canCopy={canCopy}
            />
          ) : (
            <div className="mv-modal__loading">
              <div className="spinner-border" role="status" />
              <span>Loading document…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialViewerModal;
